import { Pencil, RotateCcw } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export type MarketCompEstimate = {
  salaryMonthly: number;
  salaryAnnual: number;
  totalCashMonthly: number | null;
  totalCashAnnual: number | null;
  source: "benchmark" | "manual";
  discipline: string;
  level: string;
  currentCompany: string | null;
  currentRole: string | null;
  editedByHM: boolean;
  originalEstimate: {
    salaryMonthly: number;
    totalCashMonthly: number | null;
  } | null;
};

type MarketCompCardProps = {
  estimate: MarketCompEstimate | null;
  offerSalaryMonthly: number;
  offerEquityAnnual: number;
  offerSigningBonus: number;
  offerPerfBonus: number;
  offerPerfBonusSharesAnnual: number;
  onEstimateChange: (estimate: MarketCompEstimate) => void;
};

function fmtBRL(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
}

function fmtDelta(percent: number): string {
  const rounded = Math.round(percent);
  if (rounded === 0) return "0%";
  return rounded > 0 ? `+${rounded}%` : `${rounded}%`;
}

function deltaColor(percent: number): string {
  if (percent > 1) return "text-emerald-600";
  if (percent < -1) return "text-red-600";
  return "text-slate-600";
}

export function MarketCompCard({
  estimate,
  offerSalaryMonthly,
  offerEquityAnnual,
  offerSigningBonus,
  offerPerfBonus,
  offerPerfBonusSharesAnnual,
  onEstimateChange,
}: MarketCompCardProps) {
  const offerSalaryAnnual = offerSalaryMonthly * 12;
  const offerBonusAnnual = offerSigningBonus + offerPerfBonus;
  const offerTotalAnnual =
    offerSalaryAnnual + offerEquityAnnual + offerBonusAnnual + offerPerfBonusSharesAnnual;

  // ── Manual input (Estado 2) ────────────────────────────────────────
  const [manualSalary, setManualSalary] = useState("");
  const [manualBonus, setManualBonus] = useState("");
  const [manualSource, setManualSource] = useState("");

  const submitManual = () => {
    const salaryMonthly = parseFloat(manualSalary);
    if (!salaryMonthly || salaryMonthly <= 0) return;
    if (!manualSource.trim()) return;
    const bonusAnnual = parseFloat(manualBonus) || 0;
    const totalCashMonthly = bonusAnnual > 0 ? salaryMonthly + bonusAnnual / 12 : null;
    onEstimateChange({
      salaryMonthly,
      salaryAnnual: salaryMonthly * 12,
      totalCashMonthly,
      totalCashAnnual: totalCashMonthly !== null ? totalCashMonthly * 12 : null,
      source: "manual",
      discipline: manualSource.trim(),
      level: "",
      currentCompany: null,
      currentRole: null,
      editedByHM: true,
      originalEstimate: null,
    });
  };

  // ── Edit benchmark (Estado 3) ──────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false);
  const [editSalary, setEditSalary] = useState("");
  const [editBonus, setEditBonus] = useState("");
  const [editReason, setEditReason] = useState("");
  const editingManual = estimate?.source === "manual";

  const beginEdit = () => {
    if (!estimate) return;
    setEditSalary(String(estimate.salaryMonthly));
    setEditBonus(
      estimate.totalCashMonthly && estimate.totalCashMonthly > estimate.salaryMonthly
        ? String((estimate.totalCashMonthly - estimate.salaryMonthly) * 12)
        : "",
    );
    setEditReason(estimate.source === "manual" ? estimate.discipline : "");
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
  };

  const submitEdit = () => {
    if (!estimate) return;
    const salaryMonthly = parseFloat(editSalary);
    if (!salaryMonthly || salaryMonthly <= 0) return;
    if (!editReason.trim()) return;
    const bonusAnnual = parseFloat(editBonus) || 0;
    const totalCashMonthly = bonusAnnual > 0 ? salaryMonthly + bonusAnnual / 12 : null;
    onEstimateChange({
      ...estimate,
      salaryMonthly,
      salaryAnnual: salaryMonthly * 12,
      totalCashMonthly,
      totalCashAnnual: totalCashMonthly !== null ? totalCashMonthly * 12 : null,
      // Manual edits update the "Fonte"; benchmark edits keep discipline intact.
      discipline: estimate.source === "manual" ? editReason.trim() : estimate.discipline,
      editedByHM: true,
      // Preserve original benchmark for "restaurar" (only meaningful for benchmark source).
      originalEstimate:
        estimate.source === "manual"
          ? null
          : (estimate.originalEstimate ?? {
              salaryMonthly: estimate.salaryMonthly,
              totalCashMonthly: estimate.totalCashMonthly,
            }),
    });
    setIsEditing(false);
  };

  const restoreOriginal = () => {
    if (!estimate?.originalEstimate) return;
    const { salaryMonthly, totalCashMonthly } = estimate.originalEstimate;
    onEstimateChange({
      ...estimate,
      salaryMonthly,
      salaryAnnual: salaryMonthly * 12,
      totalCashMonthly,
      totalCashAnnual: totalCashMonthly !== null ? totalCashMonthly * 12 : null,
      editedByHM: false,
      originalEstimate: null,
    });
  };

  // ── Deltas (only meaningful in Estado 1 / view) ────────────────────
  const deltas = useMemo(() => {
    if (!estimate) return null;
    const salaryDelta =
      estimate.salaryAnnual > 0
        ? ((offerSalaryAnnual - estimate.salaryAnnual) / estimate.salaryAnnual) * 100
        : 0;
    const marketTotal = estimate.totalCashAnnual ?? estimate.salaryAnnual;
    const totalDelta = marketTotal > 0 ? ((offerTotalAnnual - marketTotal) / marketTotal) * 100 : 0;
    return { salaryDelta, totalDelta };
  }, [estimate, offerSalaryAnnual, offerTotalAnnual]);

  // ────────────────────────────────────────────────────────────────────
  // Estado 2 — sem estimativa
  // ────────────────────────────────────────────────────────────────────
  if (!estimate) {
    return (
      <div
        className="rounded-lg border bg-slate-50/50 overflow-hidden"
        data-testid="market-comp-card"
      >
        <div className="flex items-center gap-2 px-4 py-2.5 border-b bg-white">
          <span className="h-2 w-2 rounded-full bg-slate-400" aria-hidden />
          <h3 className="text-sm font-semibold text-slate-800 flex-1">Comp Atual vs. Oferta</h3>
          <span
            className="text-[10px] uppercase tracking-wide font-medium rounded-full bg-slate-200 text-slate-700 px-2 py-0.5"
            data-testid="market-badge"
          >
            Sem estimativa
          </span>
        </div>
        <div className="px-4 py-3 space-y-3">
          <p className="text-xs text-slate-600">
            Não foi possível estimar o comp de mercado para esta posição. Preencha manualmente se
            quiser comparar contra a oferta.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="market-manual-salary" className="text-xs">
                Salary mensal estimado (R$) *
              </Label>
              <Input
                id="market-manual-salary"
                type="number"
                min="0"
                step="100"
                value={manualSalary}
                onChange={(e) => setManualSalary(e.target.value)}
                placeholder="Ex.: 25000"
                data-testid="market-manual-salary"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="market-manual-bonus" className="text-xs">
                Bônus anual estimado (R$)
              </Label>
              <Input
                id="market-manual-bonus"
                type="number"
                min="0"
                step="100"
                value={manualBonus}
                onChange={(e) => setManualBonus(e.target.value)}
                placeholder="Opcional"
                data-testid="market-manual-bonus"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="market-manual-source" className="text-xs">
              Fonte *
            </Label>
            <Input
              id="market-manual-source"
              value={manualSource}
              onChange={(e) => setManualSource(e.target.value)}
              placeholder="Ex.: pretensão do candidato"
              data-testid="market-manual-source"
            />
          </div>
          <Button
            type="button"
            size="sm"
            onClick={submitManual}
            disabled={!manualSalary || !manualSource.trim()}
            data-testid="market-manual-submit"
          >
            Salvar estimativa
          </Button>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────
  // Estado 3 — editando benchmark
  // ────────────────────────────────────────────────────────────────────
  if (isEditing) {
    return (
      <div
        className="rounded-lg border bg-slate-50/50 overflow-hidden"
        data-testid="market-comp-card"
      >
        <div className="flex items-center gap-2 px-4 py-2.5 border-b bg-white">
          <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden />
          <h3 className="text-sm font-semibold text-slate-800 flex-1">Comp Atual vs. Oferta</h3>
          <span
            className="text-[10px] uppercase tracking-wide font-medium rounded-full bg-amber-100 text-amber-800 px-2 py-0.5"
            data-testid="market-badge"
          >
            Editando estimativa
          </span>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="market-edit-salary" className="text-xs">
                Salary mensal (R$)
              </Label>
              <Input
                id="market-edit-salary"
                type="number"
                min="0"
                step="100"
                value={editSalary}
                onChange={(e) => setEditSalary(e.target.value)}
                data-testid="market-edit-salary"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="market-edit-bonus" className="text-xs">
                Bônus anual (R$)
              </Label>
              <Input
                id="market-edit-bonus"
                type="number"
                min="0"
                step="100"
                value={editBonus}
                onChange={(e) => setEditBonus(e.target.value)}
                placeholder="Opcional"
                data-testid="market-edit-bonus"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="market-edit-reason" className="text-xs">
              {editingManual ? "Fonte *" : "Motivo da edição *"}
            </Label>
            {editingManual ? (
              <Input
                id="market-edit-reason"
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                placeholder="Ex.: pretensão do candidato"
                data-testid="market-edit-reason"
              />
            ) : (
              <Textarea
                id="market-edit-reason"
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                rows={2}
                placeholder="Ex.: candidato declarou pretensão acima do benchmark"
                data-testid="market-edit-reason"
              />
            )}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={submitEdit}
              disabled={!editSalary || !editReason.trim()}
              data-testid="market-edit-submit"
            >
              Confirmar
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={cancelEdit}>
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────
  // Estado 1 — benchmark exibido
  // ────────────────────────────────────────────────────────────────────
  const isManualSource = estimate.source === "manual";
  const badgeLabel = isManualSource
    ? "Manual"
    : estimate.editedByHM
      ? "P50 Benchmark (editado)"
      : "P50 Benchmark";
  const marketTotalAnnual = estimate.totalCashAnnual ?? estimate.salaryAnnual;

  return (
    <div
      className="rounded-lg border bg-slate-50/50 overflow-hidden"
      data-testid="market-comp-card"
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b bg-white">
        <span
          className={`h-2 w-2 rounded-full ${isManualSource ? "bg-slate-400" : "bg-emerald-500"}`}
          aria-hidden
        />
        <h3 className="text-sm font-semibold text-slate-800 flex-1">Comp Atual vs. Oferta</h3>
        <span
          className={`text-[10px] uppercase tracking-wide font-medium rounded-full px-2 py-0.5 ${
            isManualSource
              ? "bg-slate-200 text-slate-700"
              : estimate.editedByHM
                ? "bg-amber-100 text-amber-800"
                : "bg-emerald-100 text-emerald-800"
          }`}
          data-testid="market-badge"
        >
          {badgeLabel}
        </span>
      </div>

      <div className="px-4 py-3">
        <table className="w-full text-xs tabular-nums">
          <thead>
            <tr className="text-slate-500">
              <th className="text-left font-medium pb-1.5" />
              <th className="text-right font-medium pb-1.5">Mercado (est.)</th>
              <th className="text-right font-medium pb-1.5">Oferta</th>
              <th className="text-right font-medium pb-1.5">Delta</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-1 text-slate-700">Salary anual</td>
              <td className="py-1 text-right">{fmtBRL(estimate.salaryAnnual)}</td>
              <td className="py-1 text-right">{fmtBRL(offerSalaryAnnual)}</td>
              <td
                className={`py-1 text-right ${deltaColor(deltas?.salaryDelta ?? 0)}`}
                data-testid="market-salary-delta"
              >
                {deltas ? fmtDelta(deltas.salaryDelta) : "—"}
              </td>
            </tr>
            <tr>
              <td className="py-1 text-slate-700">Equity anual</td>
              <td className="py-1 text-right text-slate-400">—</td>
              <td className="py-1 text-right">{fmtBRL(offerEquityAnnual)}</td>
              <td className="py-1 text-right text-slate-400">—</td>
            </tr>
            <tr>
              <td className="py-1 text-slate-700">Bônus</td>
              <td className="py-1 text-right text-slate-400">—</td>
              <td className="py-1 text-right">
                {fmtBRL(offerBonusAnnual + offerPerfBonusSharesAnnual)}
              </td>
              <td className="py-1 text-right text-slate-400">—</td>
            </tr>
            <tr className="border-t border-slate-200 font-semibold">
              <td className="pt-1.5 text-slate-800">Total Comp Anual</td>
              <td className="pt-1.5 text-right">{fmtBRL(marketTotalAnnual)}</td>
              <td className="pt-1.5 text-right">{fmtBRL(offerTotalAnnual)}</td>
              <td
                className={`pt-1.5 text-right ${deltaColor(deltas?.totalDelta ?? 0)}`}
                data-testid="market-total-delta"
              >
                {deltas ? fmtDelta(deltas.totalDelta) : "—"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="bg-slate-100/60 px-4 py-2 flex items-center justify-between gap-2">
        <p className="text-[11px] text-slate-600">
          {[estimate.discipline, estimate.level].filter(Boolean).join(" · ") || "—"}
          {estimate.currentCompany ? (
            <>
              {" · "}
              {estimate.currentRole ? `${estimate.currentRole} @ ` : ""}
              {estimate.currentCompany}
            </>
          ) : null}
        </p>
        <div className="flex items-center gap-1">
          {estimate.editedByHM && estimate.originalEstimate && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11px]"
              onClick={restoreOriginal}
              data-testid="market-restore"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Restaurar original
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-[11px]"
            onClick={beginEdit}
            data-testid="market-edit"
          >
            <Pencil className="h-3 w-3 mr-1" />
            Editar estimativa
          </Button>
        </div>
      </div>
    </div>
  );
}
